import React, { useState } from 'react';
import styled from 'styled-components';
import {
    EuiButton,
    EuiFieldText,
    EuiFormRow,
    EuiSwitch
} from '@elastic/eui';

const SettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  h3 {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 4px 0;
  }

  p {
    font-size: 13.5px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0;
  }
`;

const CardContainer = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 24px;
  margin-top: 8px;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.4);
  }
`;

const SwitchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
  
  .switch-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    
    span {
      font-size: 11.5px;
      color: var(--text-muted);
      display: block;
      margin-top: 3px;
      font-weight: 400;
    }
  }
`;

const DocLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-info);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 13px;

  &:hover {
    color: var(--color-info-hover);
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const NotificationWebhookSettings = ({
    data,
    description,
    documentationUrl,
    fieldLabel,
    handleChange,
    metaData,
    placeholder,
    providerKey,
    title
}) => {
    const initialEndpoint = typeof metaData === 'string' ? metaData : '';
    const [endpoint, setEndpoint] = useState(initialEndpoint);
    const [enabled, setEnabled] = useState(!!data);

    const handleSubmit = event => {
        event.preventDefault();
        handleChange(providerKey, enabled, {
            [providerKey]: endpoint
        });
    };

    return (
        <SettingsContent>
            <h3>{ title } Notifications</h3>
            <p>
                { description }{' '}
                <DocLink href={ documentationUrl }
                         target="_blank"
                         rel="noreferrer">
                    Setup documentation →
                </DocLink>
            </p>

            <CardContainer>
                <SwitchRow>
                    <div className="switch-label">
                        Enable { title } alerts
                        <span>Receive instant notifications on { title } when alarms fire.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ enabled }
                        onChange={ () => setEnabled(!enabled) }
                    />
                </SwitchRow>

                <EuiFormRow 
                    label={ fieldLabel } 
                    fullWidth
                    helpText={`Provide your webhook or endpoint URL where alerts will be delivered.`}
                >
                    <EuiFieldText
                        value={ endpoint }
                        onChange={ event => setEndpoint(event.target.value) }
                        placeholder={ placeholder }
                        fullWidth
                    />
                </EuiFormRow>

                <ButtonContainer>
                    <EuiButton
                        isDisabled={ !endpoint }
                        size="s"
                        onClick={ handleSubmit }
                        fill
                        style={{ minWidth: '130px', borderRadius: '8px' }}
                    >
                        Save Configuration
                    </EuiButton>
                </ButtonContainer>
            </CardContainer>
        </SettingsContent>
    );
};

export default NotificationWebhookSettings;
